import type { ScriptEntry } from "./ironside-script-data";
import type { ScriptFields } from "./ironside-script-grader";

/**
 * "Not written yet" — THE DISPLAY RULE, and it is only the title.
 *
 * A slot with no row and a row with nothing in it are the same thing to a
 * reader, and they were not the same thing to the code: a draft is minted the
 * moment you touch a slot, so an empty week can hold five rows that say nothing.
 * DRAFT-JAY-CORTTY-6 is the specimen — every text field "", and a grade of 2.
 *
 * A title, and nothing else. Not "has any field", not "has been graded", not
 * "has a stage": a video is written when it has a name, and a reader scanning
 * the Videos page is looking for exactly that. Cheap to explain, cheap to
 * predict, and it cannot drift as fields come and go.
 *
 * This is NOT the same question as "is there anything to grade" — see
 * isBlankFields. A finished script whose author never titled it still grades.
 */
export function isUntitled(e: ScriptEntry): boolean {
  return !e.title.trim();
}

/**
 * Nothing to grade.
 *
 * Grading an empty document returns a 2/100 and PERSISTS it, minting a draft row
 * to hold the number — which is how DRAFT-JAY-CORTTY-6 came to exist. The whole
 * document has to be empty for this to be true, because a script with a body and
 * no title has plenty to grade.
 *
 * brandName and creatorName are excluded on purpose. They are stamped onto an
 * empty slot from its siblings before a word is typed, so a check that counted
 * them would never call anything blank.
 */
export function isBlankFields(f: ScriptFields): boolean {
  const text = [f.title, f.hook, f.scriptAngle, f.visualConcept, f.scriptBody, f.cta, f.caption, f.hashtags, f.valueProp];
  // `?? ""`, not `.trim()` straight off, because this is called on bodies that
  // came off the wire (`/api/grade`) and off rows persisted before a field
  // existed. A field this function has never heard of reads as blank, which is
  // what it is; a TypeError here 500s the grade route instead. The store
  // backfills its own rows (hydrateDraft) — this is the second line. The server's
  // submit notice reads raw draft rows through weekSlotsFrom (lib/week-slots.ts),
  // with no hydration at all, so the link list gets the same `??`.
  return text.every((v) => !(v ?? "").trim()) && (f.inspoLinks ?? []).every((l) => !(l ?? "").trim());
}

/**
 * The TEN WRITTEN FIELDS, and nothing else — `ScriptFields` without the two that
 * are not the writer's to type.
 *
 * ⚠️ `brandName` AND `creatorName` ARE LEFT OUT ON PURPOSE, and leaving them in
 * would be a real bug rather than untidiness. `ScriptFields.brandName` is the
 * brand name the CREATOR TYPED into their document (it rides on the row as
 * `brandNameProvided`, and the grader checks the hook against it), while
 * `ScriptEntry.brandName` is the row's own brand — the label beside the key. A
 * writer that spread a whole `ScriptFields` onto an entry would quietly rewrite
 * the second from the first. Anything minting or filling a row from text takes
 * this type, so there is nothing there to spread.
 */
export type DraftFields = Omit<ScriptFields, "brandName" | "creatorName">;

/** The ten, all empty. */
export function emptyDraftFields(): DraftFields {
  return {
    title: "",
    hook: "",
    scriptAngle: "",
    visualConcept: "",
    inspoLinks: [],
    scriptBody: "",
    cta: "",
    caption: "",
    hashtags: "",
    valueProp: "",
  };
}

export function fieldsFromEntry(e: ScriptEntry): ScriptFields {
  return {
    brandName: e.brandNameProvided,
    creatorName: e.creatorName,
    title: e.title,
    hook: e.hook,
    scriptAngle: e.scriptAngle,
    visualConcept: e.visualConcept,
    inspoLinks: e.inspoLinks,
    scriptBody: e.scriptBody,
    cta: e.cta,
    caption: e.caption,
    hashtags: e.hashtags,
    valueProp: e.valueProp,
  }
}
